import moment from "moment-timezone";
import { useState, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListRequestsResponse } from "@/lib/api/fund";
import { CommonDestinationTimezone, formatCurrency, cn } from "@/lib/utils";
import { formatNumberToDisplay } from "@/lib/api/common";
import { DataTable } from "@/components/ui/DataTable/index";
import { ResponsivePie } from "@nivo/pie";

interface BirthdayFundSummary {
  total_requested: number;
  total_granted: number;
  amount_granted: string;
}

interface StatsCardProps {
  title: string;
  value: string;
  description: string;
  className?: string;
  isCurrency?: boolean;
}

interface OrganizationSummaryRowProps {
  organization: string;
  summary: BirthdayFundSummary;
}

interface MonthSummaryRowProps {
  month: string;
  summary: BirthdayFundSummary;
}

export function BirthdayFundSummary({ data }: { data: ListRequestsResponse }) {
  if (!data) return null;

  const { summaries } = data;
  const organizationSummaries = Object.entries(summaries.by_organization || {}).sort((a, b) =>
    Number(b[1].amount_granted.replace(/[^0-9.-]+/g, "")) - Number(a[1].amount_granted.replace(/[^0-9.-]+/g, ""))
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard
          title="Total Requested"
          value={summaries.total_requested.toString()}
          description="Total fund requests"
          className="bg-blue-50"
        />
        <StatsCard
          title="Total Granted"
          value={summaries.total_granted.toString()}
          description="Approved fund requests"
          className="bg-green-50"
        />
        <StatsCard
          title="Amount Projected"
          value={formatCurrency(summaries.amount_projected)}
          description="Total funds projected"
          className="bg-purple-50"
          isCurrency
        />
        <StatsCard
          title="Amount Granted"
          value={formatCurrency(summaries.amount_granted)}
          description="Total funds disbursed"
          className="bg-purple-50"
          isCurrency
        />
      </div>
    </div>
  );
}

export function ByOrganizationSummary({ data }: { data: ListRequestsResponse }) {
  if (!data) return null;

  const { summaries } = data;
  const organizationSummaries = Object.entries(summaries.by_organization || {}).sort((a, b) => a[0].localeCompare(b[0]));
  const columns: any[] = [
    {
      accessorKey: "org",
      header: "Organization",
      accessorFn: (row: any) => row.org,
    },
    {
      accessorKey: "total_granted",
      header: "Total Granted",
    },
    {
      accessorKey: "total_requested",
      header: "Requested",
    },
    {
      accessorKey: "amount_granted",
      header: "Amount Granted",
      accessorFn: (row: any) => formatNumberToDisplay(row.amount_granted, { emptyFormat: "--", zeroFormat: "--", isCurrency: true }),
    },
  ]
  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">Funds by Organization</h3>
      <div className="space-y-4">
        <DataTable
          tableClassName="max-h-[680px] overflow-y-scroll"
          columns={columns}
          data={organizationSummaries.map(([org, summary]) => ({
            ...summary,
            org,
          }))}
          enableSorting
          enableMultiSort
          enableFiltering
          enableColumnVisibility
          enableGrouping
          enableExport
          stickyHeaders
          exportFilename={() => ["organization", moment().format("YYYYMMDD")].join("_")}
        />
      </div>
    </Card>
  )
}

export function ByMonthSummary({ data }: { data: ListRequestsResponse }) {
  if (!data) return null;

  const { summaries } = data;
  const monthSummaries = Object.entries(summaries.by_month || {}).sort((a, b) => {
    return moment.tz(a[0], "YYYY-MM", CommonDestinationTimezone).diff(moment.tz(b[0], "YYYY-MM", CommonDestinationTimezone))
  });

  const columns: any[] = [
    {
      accessorKey: "month",
      header: "Month",
      accessorFn: (row: any) => row.month !== 'Not Granted' ? moment.tz(row.month, "YYYY-MM", CommonDestinationTimezone).format("MMMM YYYY") : "Not Granted",
    },
    {
      accessorKey: "total_granted",
      header: "Total Granted",
    },
    {
      accessorKey: "total_requested",
      header: "Requested",
    },
    {
      accessorKey: "amount_granted",
      header: "Amount Granted",
      accessorFn: (row: any) => formatNumberToDisplay(row.amount_granted, { emptyFormat: "--", zeroFormat: "--", isCurrency: true }),
    },
  ];

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">Funds by Month</h3>
      <div className="space-y-4">
        <DataTable
          tableClassName="max-h-[680px] overflow-y-scroll"
          columns={columns}
          data={monthSummaries.map(([month, summary]) => ({
            ...summary,
            month,
          }))}
          enableSorting
          enableMultiSort
          enableFiltering
          enableColumnVisibility
          enableGrouping
          enableExport
          stickyHeaders
          exportFilename={() => ["month", moment().format("YYYYMMDD")].join("_")}
        />
      </div>
    </Card>
  )
}

function StatsCard({ title, value, description, className = "", isCurrency = false }: StatsCardProps) {
  return (
    <Card className={`p-6 ${className}`}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-3xl font-bold mt-2">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </Card>
  );
}

function OrganizationSummaryRow({ organization, summary }: OrganizationSummaryRowProps) {
  const completionPercentage = summary.total_requested > 0
    ? (summary.total_granted / summary.total_requested) * 100
    : 0;

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center space-x-2">
          <h4 className="font-medium">{organization}</h4>
          <Badge variant="outline">{summary.total_granted}/{summary.total_requested}</Badge>
        </div>
        <span className="font-semibold">{formatCurrency(summary.amount_granted)}</span>
      </div>

    </div>
  );
}


function MonthSummaryRow({ month, summary }: MonthSummaryRowProps) {
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center space-x-2">
          <h4 className="font-medium">{moment.tz(month, "YYYY-MM-DD", CommonDestinationTimezone).format("MMM Do YYYY")}</h4>
          <Badge variant="outline">{summary.total_granted}/{summary.total_requested}</Badge>
        </div>
        <span className="font-semibold">{formatCurrency(summary.amount_granted)}</span>
      </div>

    </div>
  );
}

type InsightView = "kids_by_partner" | "kids_by_organization" | "birthdays_by_destination" | "birthdays_by_age";

type AgeBracket = {
  min: number;
  max: number;
  label: string;
};

export function BirthdayFundInsights({ data }: { data: ListRequestsResponse }) {
  const [activeView, setActiveView] = useState<InsightView>("kids_by_partner");
  const [ageBracketsInput, setAgeBracketsInput] = useState<string>("1-5,6-9,10-15");

  // Parse age brackets from input - memoized and not conditional
  // Validates for exclusive, non-overlapping groups, stops at first error
  // Uses only valid brackets before the first error
  const { ageBrackets, hasBracketError } = useMemo<{ ageBrackets: AgeBracket[]; hasBracketError: boolean }>(() => {
    const brackets: AgeBracket[] = [];
    const parts = ageBracketsInput.split(',').map(s => s.trim()).filter(Boolean);
    let hasError = false;
    
    for (const part of parts) {
      
      const match = part.match(/^(\d+)-(\d+)$/);
      if (!match) {
        // Invalid format - stop processing
        hasError = true;
        break;
      }
      
      const min = parseInt(match[1]);
      const max = parseInt(match[2]);
      
      if (min > max) {
        // Invalid range - stop processing
        hasError = true;
        break;
      }
      
      // Check for overlap with existing brackets
      const overlaps = brackets.some(existing => {
        // Check if ranges overlap: (min <= existing.max && max >= existing.min)
        return min <= existing.max && max >= existing.min;
      });
      
      if (overlaps) {
        // Overlapping range - stop processing
        hasError = true;
        break;
      }
      
      // Valid bracket - add it
      brackets.push({
        min,
        max,
        label: `${min}-${max}`,
      });
    }
    
    // If no valid brackets, use defaults
    // Show error if input was provided but no valid brackets were found
    if (brackets.length === 0) {
      return {
        ageBrackets: [
          { min: 1, max: 5, label: "1-5" },
          { min: 6, max: 9, label: "6-9" },
          { min: 10, max: 15, label: "10-15" },
        ],
        hasBracketError: ageBracketsInput.trim().length > 0, // Show error if input was provided but invalid
      };
    }
    
    // If we had an error but got some valid brackets, use those and show error
    if (hasError && brackets.length > 0) {
      return {
        ageBrackets: brackets.sort((a, b) => a.min - b.min),
        hasBracketError: true,
      };
    }
    
    // All brackets valid
    return {
      ageBrackets: brackets.sort((a, b) => a.min - b.min),
      hasBracketError: false,
    };
  }, [ageBracketsInput]);

  // Extract summaries safely - memoized and not conditional
  const summaries = data?.summaries;

  // Kids Nominated by Partner/Organization - memoized and not conditional
  const partnerSummaries = useMemo(() => {
    if (!summaries) return [];
    return Object.entries(summaries.by_partner || {}).sort((a, b) => 
      b[1].unique_kids_nominated - a[1].unique_kids_nominated
    );
  }, [summaries]);

  const organizationSummaries = useMemo(() => {
    if (!summaries) return [];
    return Object.entries(summaries.by_organization || {}).sort((a, b) => 
      b[1].unique_kids_nominated - a[1].unique_kids_nominated
    );
  }, [summaries]);

  // Birthdays by Destination - memoized and not conditional
  const destinationEntries = useMemo(() => {
    if (!summaries) return [];
    return Object.entries(summaries.parties_by_destination || {});
  }, [summaries]);

  // Birthdays by Age - memoized and not conditional
  const ageEntries = useMemo(() => {
    if (!summaries) return [];
    return Object.entries(summaries.birthdays_by_age || {}).sort((a, b) => {
      const ageA = parseInt(a[0]) || 0;
      const ageB = parseInt(b[0]) || 0;
      return ageA - ageB;
    });
  }, [summaries]);

  const partnerColumns: any[] = [
    {
      accessorKey: "partner",
      header: "Nominating Partner",
      accessorFn: (row: any) => row.partner,
    },
    {
      accessorKey: "unique_kids_nominated",
      header: "Unique Kids Nominated",
    },
    {
      accessorKey: "total_requested",
      header: "Total Requested",
    },
    {
      accessorKey: "total_granted",
      header: "Total Granted",
    },
    {
      accessorKey: "amount_granted",
      header: "Amount Granted",
      accessorFn: (row: any) => formatNumberToDisplay(row.amount_granted, { emptyFormat: "--", zeroFormat: "--", isCurrency: true }),
    },
  ];

  const organizationColumns: any[] = [
    {
      accessorKey: "organization",
      header: "Nominated Organization",
      accessorFn: (row: any) => row.organization,
    },
    {
      accessorKey: "unique_kids_nominated",
      header: "Unique Kids Nominated",
    },
    {
      accessorKey: "total_requested",
      header: "Total Requested",
    },
    {
      accessorKey: "total_granted",
      header: "Total Granted",
    },
    {
      accessorKey: "amount_granted",
      header: "Amount Granted",
      accessorFn: (row: any) => formatNumberToDisplay(row.amount_granted, { emptyFormat: "--", zeroFormat: "--", isCurrency: true }),
    },
  ];

  const destinationColumns: any[] = [
    {
      accessorKey: "destination",
      header: "Destination",
      accessorFn: (row: any) => row.destination || "Not Specified",
    },
    {
      accessorKey: "count",
      header: "Number of Birthdays",
    },
  ];

  const ageColumns: any[] = [
    {
      accessorKey: "age",
      header: "Child Age",
      accessorFn: (row: any) => row.age,
    },
    {
      accessorKey: "count",
      header: "Number of Birthdays",
    },
  ];

  // Check for missing ages not covered by brackets - memoized and not conditional
  const missingAges = useMemo(() => {
    if (ageEntries.length === 0) return [];
    
    const missing: number[] = [];
    ageEntries.forEach(([ageStr]) => {
      const age = parseInt(ageStr) || 0;
      const isCovered = ageBrackets.some(bracket => age >= bracket.min && age <= bracket.max);
      if (!isCovered) {
        missing.push(age);
      }
    });
    
    return missing.sort((a, b) => a - b);
  }, [ageEntries, ageBrackets]);

  // Group age data by brackets - memoized and not conditional
  const bracketData = useMemo(() => {
    const bracketCounts: Record<string, number> = {};
    
    // Initialize all brackets with 0
    ageBrackets.forEach(bracket => {
      bracketCounts[bracket.label] = 0;
    });
    
    // Count birthdays in each bracket
    ageEntries.forEach(([ageStr, count]) => {
      const age = parseInt(ageStr) || 0;
      const bracket = ageBrackets.find(b => age >= b.min && age <= b.max);
      if (bracket) {
        bracketCounts[bracket.label] = (bracketCounts[bracket.label] || 0) + count;
      }
    });
    
    return bracketCounts;
  }, [ageEntries, ageBrackets]);

  // Generate pie chart data for birthdays by age brackets - memoized and not conditional
  const totalBirthdays = useMemo(() => {
    return ageEntries.reduce((sum, [, count]) => sum + count, 0);
  }, [ageEntries]);

  const pieChartData = useMemo(() => {
    const colors = [
      "#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", 
      "#ef4444", "#06b6d4", "#84cc16", "#f97316", "#6366f1"
    ];
    
    return ageBrackets.map((bracket, index) => {
      const count = bracketData[bracket.label] || 0;
      const percent = totalBirthdays > 0 ? (count / totalBirthdays) * 100 : 0;
      return {
        id: bracket.label,
        label: `Ages ${bracket.label}`,
        value: count,
        percent: percent,
        color: colors[index % colors.length],
      };
    }).filter(item => item.value > 0); // Only show brackets with data
  }, [ageBrackets, bracketData, totalBirthdays]);

  const viewButtons: { view: InsightView; label: string }[] = [
    { view: "kids_by_partner", label: "Kids by Partner" },
    { view: "kids_by_organization", label: "Kids by Organization" },
    { view: "birthdays_by_destination", label: "Birthdays by Destination" },
    { view: "birthdays_by_age", label: "Birthdays by Age" },
  ];

  if (!data || !summaries) return null;

  return (
    <div className="space-y-6">
      {/* Average Birthday Cost Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatsCard
          title="Average Birthday Cost"
          value={summaries.birthday_cost_average ? formatCurrency(summaries.birthday_cost_average) : "N/A"}
          description={`Based on ${summaries.birthday_cost_count || 0} birthdays (excluding $0 grants)`}
          className="bg-purple-50"
          isCurrency
        />
        {summaries.birthday_cost_median && (
          <StatsCard
            title="Median Birthday Cost"
            value={formatCurrency(summaries.birthday_cost_median)}
            description="Median birthday cost"
            className="bg-blue-50"
            isCurrency
          />
        )}
      </div>

      {/* View Switcher */}
      <div className="flex flex-wrap gap-2">
        {viewButtons.map(({ view, label }) => (
          <Button
            key={view}
            variant="outline"
            size="sm"
            onClick={() => setActiveView(view)}
            className={cn(
              "h-9",
              activeView === view && "bg-primary text-primary-foreground hover:bg-primary/90"
            )}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Kids Nominated by Partner */}
      {activeView === "kids_by_partner" && (
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Kids Nominated by Nominating Partner</h3>
          <div className="space-y-4">
            {partnerSummaries.length > 0 ? (
              <DataTable
                tableClassName="max-h-[680px] overflow-y-scroll"
                columns={partnerColumns}
                data={partnerSummaries.map(([partner, summary]) => ({
                  ...summary,
                  partner,
                }))}
                enableSorting
                enableMultiSort
                enableFiltering
                enableColumnVisibility
                enableGrouping
                enableExport
                stickyHeaders
                defaultSorting={[{ id: "partner", desc: false }]}
                exportFilename={() => ["kids_by_partner", moment().format("YYYYMMDD")].join("_")}
              />
            ) : (
              <p className="text-muted-foreground">No partner data available</p>
            )}
          </div>
        </Card>
      )}

      {/* Kids Nominated by Organization */}
      {activeView === "kids_by_organization" && (
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Kids Nominated by Nominated Organization</h3>
          <div className="space-y-4">
            {organizationSummaries.length > 0 ? (
              <DataTable
                tableClassName="max-h-[680px] overflow-y-scroll"
                columns={organizationColumns}
                data={organizationSummaries.map(([organization, summary]) => ({
                  ...summary,
                  organization,
                }))}
                enableSorting
                enableMultiSort
                enableFiltering
                enableColumnVisibility
                enableGrouping
                enableExport
                stickyHeaders
                defaultSorting={[{ id: "organization", desc: false }]}
                exportFilename={() => ["kids_by_organization", moment().format("YYYYMMDD")].join("_")}
              />
            ) : (
              <p className="text-muted-foreground">No organization data available</p>
            )}
          </div>
        </Card>
      )}

      {/* Birthdays by Destination */}
      {activeView === "birthdays_by_destination" && (
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Birthdays Celebrated by Destination</h3>
          <div className="space-y-4">
            {destinationEntries.length > 0 ? (
              <DataTable
                tableClassName="max-h-[680px] overflow-y-scroll"
                columns={destinationColumns}
                data={destinationEntries.map(([destination, count]) => ({
                  destination,
                  count,
                }))}
                enableSorting
                enableMultiSort
                enableFiltering
                enableColumnVisibility
                enableGrouping
                enableExport
                stickyHeaders
                defaultSorting={[{ id: "destination", desc: false }]}
                exportFilename={() => ["birthdays_by_destination", moment().format("YYYYMMDD")].join("_")}
              />
            ) : (
              <p className="text-muted-foreground">No destination data available</p>
            )}
          </div>
        </Card>
      )}

      {/* Birthdays by Age */}
      {activeView === "birthdays_by_age" && (
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-lg font-medium">Birthdays by Child's Age</h3>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Label htmlFor="age-brackets" className="text-sm font-medium whitespace-nowrap">
                  Age Brackets:
                </Label>
                <Input
                  id="age-brackets"
                  type="text"
                  value={ageBracketsInput}
                  onChange={(e) => setAgeBracketsInput(e.target.value)}
                  placeholder="1-5,6-9,10-15"
                  className={cn(
                    "w-full sm:w-48",
                    hasBracketError && "ring-red-500 focus:ring-red-500 border-red-500"
                  )}
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Format: min-max,min-max
                </span>
              </div>
              {missingAges.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-600" />
                  <span>
                    Missing ages from brackets: {missingAges.join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            {ageEntries.length > 0 ? (
              <div className="space-y-6">
                {/* Pie Chart */}
                {pieChartData.length > 0 ? (
                  <div className="flex flex-col lg:flex-row gap-6 items-start">
                    <div className="flex-shrink-0">
                      <div className="w-64 h-64">
                        <ResponsivePie
                          data={pieChartData}
                          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                          innerRadius={0.4}
                          padAngle={2}
                          cornerRadius={3}
                          activeOuterRadiusOffset={4}
                          colors={(d) => d.data.color}
                          borderWidth={1}
                          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                          enableArcLinkLabels={true}
                          arcLinkLabelsSkipAngle={10}
                          arcLinkLabelsTextColor="#333333"
                          arcLinkLabelsThickness={2}
                          arcLinkLabelsColor={{ from: 'color' }}
                          enableArcLabels={true}
                          arcLabelsSkipAngle={10}
                          arcLabelsTextColor="#ffffff"
                          isInteractive={true}
                          tooltip={({ datum }) => (
                            <div className="bg-white p-2 shadow-lg rounded border text-xs">
                              <div className="font-semibold">{datum.label}</div>
                              <div>
                                {`${datum.value} birthdays (${formatNumberToDisplay(datum.data.percent, { suffix: "%", maximumFractionDigits: 1 })})`}
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {pieChartData.map((item) => (
                          <div key={item.id} className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: item.color }}
                            ></div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">
                                {item.label} <span className="text-xs text-muted-foreground">
                                  ({formatNumberToDisplay(item.percent, { suffix: "%", maximumFractionDigits: 1 })})
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground">{item.value} birthdays</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No data available for the specified age brackets</p>
                )}
                {/* Data Table */}
                <DataTable
                  tableClassName="max-h-[680px] overflow-y-scroll"
                  columns={ageColumns}
                  data={ageEntries.map(([age, count]) => ({
                    age,
                    count,
                  }))}
                  enableSorting
                  enableMultiSort
                  enableFiltering
                  enableColumnVisibility
                  enableGrouping
                  enableExport
                  stickyHeaders
                  exportFilename={() => ["birthdays_by_age", moment().format("YYYYMMDD")].join("_")}
                />
              </div>
            ) : (
              <p className="text-muted-foreground">No age data available</p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
